import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useTilt } from "../utils/useTilt";
import Doctor1 from "../assets/Doktor1.jpeg";
import Doctor2 from "../assets/Doktor.2.jpeg";

const photos = [Doctor1, Doctor2];

interface Doctor {
  name: string;
  title: string;
  description: string;
}

const DoctorCard: React.FC<{ doctor: Doctor; photo: string; idx: number }> = ({
  doctor,
  photo,
  idx,
}) => {
  const { tilt, onMouseMove, onMouseLeave } = useTilt(5);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      animate={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY }}
      transition={{
        rotateX: { type: "spring", stiffness: 200, damping: 18 },
        rotateY: { type: "spring", stiffness: 200, damping: 18 },
        default: { duration: 0.6, delay: idx * 0.1 },
      }}
      style={{ transformStyle: "preserve-3d", transformPerspective: 1000 }}
      className="group bg-gradient-to-b from-white to-gray-100 rounded-2xl shadow-md hover:shadow-2xl transition-shadow duration-300 p-6 flex flex-col items-center text-center"
    >
      <div className="relative w-48 h-48 mb-6" style={{ transform: "translateZ(30px)" }}>
        <img
          src={photo}
          alt={doctor.name}
          loading="lazy"
          className="w-full h-full object-cover rounded-full border-4 border-yellow-500 shadow-md group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 rounded-full ring-2 ring-transparent group-hover:ring-yellow-400 transition-all duration-300"></div>
      </div>

      <h3 className="text-2xl font-semibold text-black mb-1">{doctor.name}</h3>
      <p className="text-yellow-600 font-medium mb-4">{doctor.title}</p>
      <p className="text-gray-700 leading-relaxed">{doctor.description}</p>
    </motion.div>
  );
};

const DoctorsSection: React.FC = () => {
  const { t } = useTranslation();

  // i18n'den doktor listesini alıyoruz
  const doctors = t("doctors.list", { returnObjects: true }) as Doctor[];

  return (
    <section className="bg-amber-50/60 py-20" style={{ perspective: 1200 }}>
      <div className="max-w-7xl mx-auto px-6">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-yellow-600 font-semibold text-sm uppercase tracking-widest text-center mb-3"
      >
        {t("doctors.eyebrow")}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-4xl font-bold mb-16 text-center text-black"
      >
        {t("doctors.title")}
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {doctors.map((doctor, idx) => (
          <DoctorCard key={idx} doctor={doctor} photo={photos[idx]} idx={idx} />
        ))}
      </div>
      </div>
    </section>
  );
};

export default DoctorsSection;
